/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-25 09:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { reduxForm, reset, getFormValues, change, untouch } from 'redux-form';
import {
  Page,
  PageActions,
  Layout,
  Stack,
  Button,
  Card,
  Tabs,
  Icon,
  Link,
  SkeletonBodyText,
  SkeletonDisplayText
} from '@shopify/polaris';
import { LockMinor, ExternalSmallMinor } from '@shopify/polaris-icons';

import SearchResultsTab from 'src/components/views/search-page-display/search-results-tab';
import NoSearchResultsTab from 'src/components/views/search-page-display/no-search-results-tab';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import InvalidInputBanner from 'src/components/views/search-page-display/invalid-input-banner';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import UpgradeThemeBanner from 'src/components/containers/common/switchNewVersion/theme/upgrade-theme-banner';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';
import { FEATURE_IDS } from 'src/constants/theme-upgrade';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/search-page-display/did-you-know';

import { URL } from 'src/constants/links';
import history from 'src/routes/history';
import {
  buildInitialSearchPageDisplayValues,
  checkValidateSearchPageDisplay,
  buildSearchPageDisplayValues
} from 'src/helpers/search-page-display-helper';
import { confirmLeavePage } from 'src/helpers';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';

import { routes } from 'src/routes';

import { hasThemeV3 } from 'src/helpers/theme-setup-helper';
import * as FilterSettingsActions from 'actions/filter-settings-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import * as TranslationActions from 'actions/translation-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import LockImage from 'src/assets/images/lock-unlock-image/search-no-result-lock.svg';

const SearchResultPage = props => {
  const {
    i18n,
    dirty,
    submitting,
    handleSubmit,
    pristine,
    saving,
    loading,
    collections,
    collectionsActive,
    pagesActive,
    allowSearchV2,
    themeSetupV2,
    searchTipsActive,
    searchTermSuggestionsActive,
    searchTermSuggestionsType,
    searchTermSuggestionsList,
    mostPopularProductsActive,
    mostPopularProductsType,
    mostPopularProductsList,
    updateField,
    formValues,
    themes,
    untouchForm,
    initialValues,
    updateFilterSettings,
    filterSettings,
    primaryLocale,
    useBoltOn,
    getFilterSettings,
    getFilterData,
    getThemesLoading,
    getLocales,
    getThemes,
    enabledFeature,
    markDoneStep
  } = props;

  const [activeRequestModal, setActiveRequestModal] = useState(false);
  const [activeResultModal, setActiveResultModal] = useState(false);
  const [youtubeModal, setYoutubeModal] = useState(false);
  const [selected, setSelected] = useState(0);
  const [dismissedBanner, setDismissedBanner] = useState(true);
  const [messageError, setMessageError] = useState('');
  const [changePlanFromReviewIOModal, setChangePlanFromReviewIOModal] = useState(false);

  const upgradedTheme = hasThemeV3(themes);

  // function
  /**
   * Swap options between display based on data and manually setting
   * Mainly idea is keep previous setting for customer when swap between 2 options
   * @param {*} type
   */
  const swapOptionValues = (type: string) => {
    if (type === 'searchTerm') {
      const searchTermSuggestions = _.get(formValues, 'searchTermSuggestions', {});
      let searchTerms = _.get(searchTermSuggestions, 'searchTermList', []);
      let backup = _.get(searchTermSuggestions, 'backup', []);
      // Swap backup data and main data
      [backup, searchTerms] = [searchTerms, backup];

      updateField('searchTermSuggestions.searchTermList', searchTerms);
      updateField('searchTermSuggestions.backup', backup);
    } else if (type === 'product') {
      const mostPopularProducts = _.get(formValues, 'mostPopularProducts', {});
      let products = _.get(mostPopularProducts, 'productList', []);
      let backup = _.get(mostPopularProducts, 'backup', []);
      // Swap backup data and main data
      [backup, products] = [products, backup];

      updateField('mostPopularProducts.productList', products);
      updateField('mostPopularProducts.backup', backup);
    }
  };
  // Handle click save button
  const handleSave = async data => {
    const validateSearchPageDisplay = checkValidateSearchPageDisplay(data);
    if (!_.isEmpty(validateSearchPageDisplay)) {
      setDismissedBanner(false);
      setMessageError(validateSearchPageDisplay);
      return;
    }

    setDismissedBanner(true);
    setMessageError('');
    // Build new settings
    const savedData = buildSearchPageDisplayValues(data, filterSettings, primaryLocale);
    const productList = _.get(
      savedData,
      'search.searchPanelBlocks.mostPopularProducts.productList',
      []
    );
    const productBackup = _.get(
      savedData,
      'search.searchPanelBlocks.mostPopularProducts.backup',
      []
    );

    // Save new settings
    await updateFilterSettings(savedData, productList, productBackup);

    markDoneStep('V2_SEARCH1', {
      isDone: true
    });
  };

  // Handle discard button
  const discardChange = () => {
    const { resetForm } = props;
    resetForm();
  };

  // Handle hide banner
  const handleDismissBanner = () => {
    // Hide banner
    setDismissedBanner(true);
    setMessageError(false);
  };
  // Handle confirm leave page
  const handleConfirmLeavePage = event => {
    const { dirty } = props;
    return confirmLeavePage(event, dirty, i18n);
  };

  /**
   * only trigger when disable toggle
   * @param {*} data
   * @param {*} field
   */
  const resetField = field => {
    const object = _.get(initialValues, field, {});

    const status = _.get(object, 'active', false);
    updateField(field, { ...object, active: false });
    if (!status) {
      untouchForm(field);
    }
  };

  const renderLockFeature = i18n => {
    return (
      <div className="pfs-feature-lock-card">
        <img src={LockImage} alt="in-collection-search-lock" />
        <div className="content">
          {i18n.translate(`AdminHTML.SearchPageDisplay.Plan.lockCard_Content01`)}
        </div>
        <p>{i18n.translate(`AdminHTML.SearchPageDisplay.Plan.lockCard_Content02`)}</p>
        <div className="action">
          <div
            className={`Polaris-Button`}
            onClick={() =>
              useBoltOn
                ? setChangePlanFromReviewIOModal(true)
                : history.push(routes.subscription.package())
            }>
            <Icon source={LockMinor} />
            {i18n.translate(`AdminHTML.Common.Action.upgradeToUnlock`)}
          </div>
          <Link external url={URL['Link_V2_Search_SearchPageDisplay_ManualGuide']}>
            {i18n.translate('AdminHTML.Common.Action.learnMore')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        </div>
      </div>
    );
  };

  // Render banners for search result page
  const renderBanners = () => {
    if (getThemesLoading) {
      return (
        <>
          <SkeletonBodyText lines={1} />
          <SkeletonDisplayText size="small" />
          <SkeletonDisplayText size="small" />
        </>
      );
    }
    return (
      <>
        {/* Did you know */}
        {/* Upgrade theme banner */}

        {!themeSetupV2 && !upgradedTheme ? <UpgradeThemeBanner /> : <DidYouKnow />}

        {!_.isEmpty(themes) && (
          <ThemeUpgradeFlow2Notification
            notAllowGetThemes={true}
            featureID={FEATURE_IDS.SEARCH_PAGE_DISPLAY}
          />
        )}
        {/* // New feature0423: old customer flow - migrate */}

        {/* Banner error */}
        {!dismissedBanner && (
          <InvalidInputBanner
            onDismiss={handleDismissBanner}
            message={i18n.translate(messageError)}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    // Get locales
    getLocales();

    getFilterSettings();

    getFilterData();

    if (_.isEmpty(themes)) getThemes();
    // Handle when reload or leave page
    window.addEventListener('beforeunload', handleConfirmLeavePage);

    return () => {
      // Handle when reload or leave page
      window.removeEventListener('beforeunload', handleConfirmLeavePage);
    };
  }, []);

  const tabs = [
    {
      id: 'search_results_page',
      content: i18n.translate('AdminHTML.SearchPageDisplay.SearchResultsPage.title'),
      panelID: 'search_results_page'
    },
    {
      id: 'no_search_results_page',
      content: i18n.translate('AdminHTML.SearchPageDisplay.NoSearchResultsPage.title'),
      panelID: 'no_search_results_page'
    }
  ];
  const tabComponents = [
    <SearchResultsTab
      updateField={updateField}
      loading={loading}
      formValues={formValues}
      collections={collections}
      collectionsActive={collectionsActive}
      pagesActive={pagesActive}
      allowSearchV2={allowSearchV2}
      hasThemeV3={upgradedTheme}
    />,
    <NoSearchResultsTab
      updateField={updateField}
      loading={loading}
      resetField={resetField}
      searchTipsActive={searchTipsActive}
      swapOptionValues={swapOptionValues}
      searchTermSuggestionsActive={searchTermSuggestionsActive}
      searchTermSuggestionsType={searchTermSuggestionsType}
      searchTermSuggestionsList={searchTermSuggestionsList}
      mostPopularProductsActive={mostPopularProductsActive}
      mostPopularProductsType={mostPopularProductsType}
      mostPopularProductsList={mostPopularProductsList}
    />
  ];

  return (
    <div className="BC-Page BC-Page__SearchPageDisplay">
      <Page
        title={i18n.translate('AdminHTML.SearchPageDisplay.SearchPageDisplay.newTitle')}
        primaryAction={
          <div className="BC-Page BC-Page__Button">
            <Stack>
              {!pristine && (
                <Button disabled={submitting} onClick={discardChange}>
                  {i18n.translate('AdminHTML.Common.Action.discard')}
                </Button>
              )}
              <Button
                primary
                onClick={handleSubmit(handleSave.bind(this))}
                disabled={pristine || submitting}
                loading={saving && !pristine}>
                {i18n.translate('AdminHTML.Common.Action.saveSettings')}
              </Button>
            </Stack>
          </div>
        }>
        <TitleHelp
          i18n={i18n}
          onPlay={() => setYoutubeModal(true)}
          hrefUserManual={URL['Link_V2_Search_SearchPageDisplay_ManualGuide']}
          onAnalytics={() => history.push(routes.analytics.overviewSearch())}
        />
        <Layout>
          <Layout.Section>
            <div className="BC-Container__Notifications">{renderBanners()}</div>
          </Layout.Section>

          <Layout.Section>
            <div className="Children-Card__Section BC-Page__Fields">
              <Tabs tabs={tabs} selected={selected} onSelect={value => setSelected(value)}>
                {!enabledFeature && selected === 1 ? (
                  renderLockFeature(i18n)
                ) : (
                  <Card.Section>
                    <div className="tab-content">{tabComponents[selected]}</div>
                  </Card.Section>
                )}
              </Tabs>
            </div>
          </Layout.Section>
        </Layout>
        <PageActions
          primaryAction={{
            content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
            onAction: handleSubmit(handleSave.bind(this)),
            disabled: pristine || submitting,
            loading: saving && !pristine
          }}
          secondaryActions={
            !pristine
              ? [
                  {
                    content: i18n.translate('AdminHTML.Common.Action.discard'),
                    onAction: discardChange,
                    disabled: submitting
                  }
                ]
              : []
          }
        />
      </Page>
      <SendRequestFeatureModal
        active={activeRequestModal}
        handleClose={() => setActiveRequestModal(false)}
        handleOpenResultModal={() => setActiveRequestModal(true)}
        featureKey="searchPageDisplay"
      />
      <RequestResultModal
        active={activeResultModal}
        handleClose={() => setActiveResultModal(false)}
        featureName={i18n.translate(
          'AdminHTML.Common.RequestResultModal.content_t1_searchPageDisplay'
        )}
      />

      <YoutubeModal
        active={youtubeModal}
        embeddedURL={URL['Link_V2_Search_SearchPageDisplay_Video']}
        onClose={() => setYoutubeModal(false)}
        title={i18n.translate('AdminHTML.SearchPageDisplay.SearchPageDisplay.youtubeModal_Title')}
      />

      {/* Confirm before leaving */}
      <RouteLeavingGuard
        when={dirty}
        // Navigate function
        navigate={path => history.push(path)}
        shouldBlockNavigation={dirty}
      />
      <ChangePlanFromReviewIOModal
        open={changePlanFromReviewIOModal}
        onClose={() => setChangePlanFromReviewIOModal(false)}
      />
    </div>
  );
};

const SearchResultPageForm = reduxForm({
  form: 'SearchResultPageForm', // a unique identifier for this form
  enableReinitialize: true
})(SearchResultPage);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n)),
  getFilterSettings: () => dispatch(FilterSettingsActions.getFilterSettings(true, ownProps.i18n)),
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),

  updateFilterSettings: (
    filterSettings: Object,
    productSuggestionsList: Array<Object>,
    productBackup: Array<Object>
  ) =>
    dispatch(
      FilterSettingsActions.updateFilterSettings(
        filterSettings,
        'searchPageDisplay',
        { suggestionsList: { productSuggestionsList, productBackup } },
        ownProps.i18n
      )
    ),
  updateField: (field, value) => dispatch(change(`SearchResultPageForm`, field, value)),
  // Locales
  getLocales: () => dispatch(TranslationActions.getLocales(ownProps.i18n)),
  resetForm: () => dispatch(reset('SearchResultPageForm')),
  untouchForm: field => dispatch(untouch('SearchResultPageForm', field)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { filterSettings, translation } = state;
  const formValues = getFormValues('SearchResultPageForm')(state);

  const pagesActive = _.get(formValues, 'pages.active', false);
  const collectionsActive = _.get(formValues, 'collections.active', false);

  /**
   * search tip
   */
  const searchTipsActive = _.get(formValues, 'searchTips.active', false);

  /**
   * search term suggestions
   */
  const searchTermSuggestionsActive = _.get(formValues, 'searchTermSuggestions.active', false);
  const searchTermSuggestionsType = _.get(
    formValues,
    'searchTermSuggestions.type',
    'based_on_data'
  );
  const searchTermSuggestionsList = _.get(formValues, 'searchTermSuggestions.searchTermList', []);

  /**
   * search most popular products
   */
  const mostPopularProductsActive = _.get(formValues, 'mostPopularProducts.active', false);
  const mostPopularProductsType = _.get(formValues, 'mostPopularProducts.type', 'based_on_data');
  const mostPopularProductsList = _.get(formValues, 'mostPopularProducts.productList', []);

  /**
   * initial value for form
   */
  const initialValues = buildInitialSearchPageDisplayValues(
    filterSettings.filterSettings,
    props.i18n
  );

  // Detect primary locale
  const primaryLocale = _.get(translation, 'primaryLocale', 'en');
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const allowSearchV2 = allowUsingNewSearchSettings(searchVersion);

  return {
    formValues,
    enabledFeature: _.get(state.oauth.tenantApp, 'settings.search.settings.redirects', true),
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    collections: _.get(state.filterData.filterData, 'collections', []),
    themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false),
    themes: _.get(state.themeSetup, 'themes', []),
    getThemesLoading: _.get(state.themeSetup, 'getThemesLoading', false),
    filterSettings: filterSettings.filterSettings,
    saving: filterSettings.savingFilterSettings,
    loading: filterSettings.loadingFilterSettings,
    initialValues,
    collectionsActive,
    pagesActive,
    searchTipsActive,
    searchTermSuggestionsActive,
    searchTermSuggestionsType,
    searchTermSuggestionsList,
    mostPopularProductsActive,
    mostPopularProductsType,
    mostPopularProductsList,
    primaryLocale,
    allowSearchV2
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(SearchResultPageForm)),
  'MapProduct'
);
